import { useId, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useSubmit } from '../../hooks/useSubmit';
import { createVoucher } from '../adminApi';
import { useAdmin } from '../AdminContext';
import { PageHeader } from '../ui/PageHeader';
import { Panel } from '../ui/Panel';
import { Field } from '../ui/Field';
import { AutoTextarea } from '../ui/AutoTextarea';
import { Spinner } from '../../ui/Spinner';
import { btnPrimary, btnSecondary, inputCls } from '../ui/styles';
import { useRequestId } from '../../hooks/useRequestId';
import { useUnsavedWork } from '../../contexts/UnsavedWorkContext';
import { money } from '../../core/format/currency';
import { GIFTCARD_MAX_CENTS, GIFTCARD_MIN_CENTS } from '../../core/rules/kassa';
import { parseEuroInput, validForLabel } from './voucherLabels';
import type { VoucherValidFor } from '../../core/types/vouchers';

/**
 * Een bon met de hand maken (winactie, compensatie, verkocht aan de balie zolang de kassa er niet is).
 * Transactiescherm: één `requestId` per inhoud, en pas na het antwoord van de server naar de bon.
 */
export function VoucherNew() {
  const navigate = useNavigate();
  const {
    site
  } = useAdmin();
  const amountId = useId();
  const [amount, setAmount] = useState('');
  const [validFor, setValidFor] = useState<VoucherValidFor>(site.policy.giftCardValidFor);
  const [note, setNote] = useState('');
  const [touched, setTouched] = useState(false);
  const {
    submit,
    submitting,
    error,
    fieldErrors
  } = useSubmit(createVoucher);
  const cents = parseEuroInput(amount);
  const amountProblem = cents === null ? 'Vul een bedrag in, bijvoorbeeld 50 of 37,50.' : cents < GIFTCARD_MIN_CENTS ? `Minimaal ${money(GIFTCARD_MIN_CENTS)}.` : cents > GIFTCARD_MAX_CENTS ? `Maximaal ${money(GIFTCARD_MAX_CENTS)}.` : null;
  const shownAmountError = fieldErrors.initialCents ?? (touched ? amountProblem : null);

  // Zelfde inhoud = zelfde poging: na een verloren antwoord ontstaat er geen tweede bon.
  const attempt = `${cents}|${validFor}|${note.trim()}`;
  const requestId = useRequestId(attempt);
  // Half ingevulde bon: eerst "Weggaan?" bij doorklikken of het tabblad sluiten.
  useUnsavedWork(Boolean(amount.trim() || note.trim()) && !submitting, 'page');
  const months = site.policy.giftCardValidMonths;
  const summary = useMemo(() => cents && !amountProblem ? `${money(cents)} · ${validForLabel[validFor].toLowerCase()} · ${months} maanden geldig` : null, [cents, amountProblem, validFor, months]);
  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (amountProblem || cents === null) {
      document.getElementById(amountId)?.focus();
      return;
    }
    const v = await submit({
      requestId,
      initialCents: cents,
      validFor,
      note: note.trim()
    });
    if (!v) return;
    toast.success('Cadeaubon gemaakt', {
      description: 'Print hem of stuur de code door.'
    });
    navigate(`/be/cadeaubonnen/${v.id}`, {
      replace: true
    });
  };
  return <div className="max-w-2xl" data-id="element-5624" data-mp="kd0qr9mwobox">
      <PageHeader title="Nieuwe cadeaubon" description="De code maakt de server; die verschijnt zodra de bon is aangemaakt." back={{
      to: '/be/cadeaubonnen',
      label: 'Alle cadeaubonnen'
    }} data-id="element-5625" data-mp="ffs52z2jtpix" />
      <form onSubmit={e => void onSubmit(e)} noValidate data-id="element-5626" data-mp="u6hj097e9iqh">
        <Panel data-id="element-5627" data-mp="p6wn67nitqhz">
          <div className="space-y-5" data-id="element-5628" data-mp="p6db37kjxwbv">
            <Field label="Bedrag" hint="In euro’s. Deels besteden mag; het restant blijft op de bon." data-id="element-5629" data-mp="1fr2nf6mya1c">
              <div className="relative sm:max-w-[12rem]" data-id="element-5630" data-mp="3smtwkeduyuf9">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[14px] text-muted" aria-hidden="true" data-id="element-5631" data-mp="3soeri5434fdn">
                  €
                </span>
                <input id={amountId} inputMode="decimal" value={amount} onChange={e => setAmount(e.target.value)} onBlur={() => setTouched(true)} placeholder="50" aria-invalid={shownAmountError ? true : undefined} aria-describedby={shownAmountError ? `${amountId}-error` : undefined} className={`${inputCls} pl-7 tabular-nums`} data-id="element-5632" data-mp="6diwwfur688p" />
              </div>
              {shownAmountError && <p id={`${amountId}-error`} className="mt-1.5 text-[12px] text-clay" data-id="element-5633" data-mp="2r4gry03ucxs4">
                  {shownAmountError}
                </p>}
            </Field>

            <fieldset data-id="element-5634" data-mp="304n2pe85njnl">
              <legend className="text-[13px] font-medium text-ink" data-id="element-5635" data-mp="3043ifqv18j6e">Geldt voor</legend>
              <div className="mt-2 space-y-2 text-[14px]" data-id="element-5636" data-mp="34v3gizfw17ah">
                {(['all', 'treatments'] as const).map(k => <label key={k} className="flex items-center gap-2" data-id="element-5637" data-mp="39tn5l8ps2z72">
                    <input type="radio" name="valid-for" checked={validFor === k} onChange={() => setValidFor(k)} className="h-4 w-4 accent-clay" data-id="element-5638" data-mp="3eub4sho5umcd" />
                    {k === 'all' ? 'Behandelingen en producten in de webshop' : 'Alleen behandelingen in de salon'}
                  </label>)}
              </div>
            </fieldset>

            <Field label="Notitie" hint="Alleen voor jou: waarom deze bon bestaat. Komt niet op de bon." data-id="element-5639" data-mp="1354l7qt0ce12">
              <AutoTextarea value={note} onChange={e => setNote(e.target.value)} minRows={2} maxLength={300} className={inputCls} data-id="element-5640" data-mp="y7l5snkkpv7j" />
            </Field>
          </div>
        </Panel>

        {error && !fieldErrors.initialCents && <p role="alert" className="mt-4 rounded-md bg-blush/50 px-3 py-2 text-[13px] text-ink" data-id="element-5641" data-mp="1lx77pisbyoj8">
            {error}
          </p>}

        <div className="mt-5 flex flex-wrap items-center gap-3" data-id="element-5642" data-mp="1h4w3w4ugyile">
          <button type="submit" disabled={submitting} aria-busy={submitting} className={btnPrimary} data-id="element-5643" data-mp="1c7noud4mauod">
            {submitting && <Spinner data-id="element-5644" data-mp="29njm9ci2zjis" />}
            {submitting ? 'Bon maken…' : 'Bon maken'}
          </button>
          <button type="button" onClick={() => navigate('/be/cadeaubonnen')} disabled={submitting} className={btnSecondary} data-id="element-5645" data-mp="24uqiorycmh8a">
            Niet maken
          </button>
          {summary && <span className="text-[13px] text-muted" data-id="element-5646" data-mp="7xu8r463dr3z">{summary}</span>}
        </div>
      </form>
    </div>;
}